import { BotSequenceConfigStep, BotSequenceMode, BotSequenceType, FMSCapabilityKey, FMSObjectType, FeatureFlagKey } from "@swivl-mono/types";
import { useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

import { isFeatureEnabled } from "../../../Helpers/FeatureFlags/isFeatureEnabled";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { PROVIDER_LABELS } from "../../Admin/FMSCapabilities/JitFreshness.Shared";
import { DayCapabilityNotes } from "./Ladder/DaySettings";
import { SaveBar } from "./SaveBar";
import { LadderSection } from "./Sections/LadderSection";
import { ModeSection } from "./Sections/ModeSection";
import { OPT_IN_CAPABILITY, TypeSettingsSection } from "./Sections/TypeSettingsSection";
import { isSequenceTypeVisible } from "./SequenceTypeList";
import { useSequencesV3Context } from "./SequencesV3.Context";
import { SEQUENCE_TYPE_META, sequenceTypeForSlug } from "./sequenceTypeMeta";

const NO_EMAIL_SENDER_REASON = "No Mandrill app is set up for this organization (Settings → Apps → Mandrill).";

/** Why the FMS note is or is not available for this type at this scope (decisions 18/25). */
function fmsNoteCapability(
  type: BotSequenceType,
  fms: ReturnType<typeof useSequencesV3Context>["fms"],
  scopeKind: "org" | "group" | "location"
): Omit<DayCapabilityNotes, "emailDisabledReason"> {
  const objectType = type === BotSequenceType.ABANDONED_CART ? FMSObjectType.Lead : FMSObjectType.Tenant;
  const coverage = fms.coverage(FMSCapabilityKey.NoteWrite, objectType);
  if (type === BotSequenceType.ABANDONED_CART && coverage === "none") {
    return { fmsNoteDisabledReason: "Abandoned-cart contacts are leads; notes can only be written to tenants.", fmsNoteInfo: null };
  }
  if (scopeKind === "location") {
    if (fms.locationCount === 0 || fms.fmsTypes.length === 0) {
      return { fmsNoteDisabledReason: "This location has no FMS integration.", fmsNoteInfo: null };
    }
    if (coverage !== "all") {
      const label = fms.singleFmsType ? (PROVIDER_LABELS[fms.singleFmsType] ?? fms.singleFmsType) : "This FMS";
      return { fmsNoteDisabledReason: `${label} does not support writing tenant notes.`, fmsNoteInfo: null };
    }
    return { fmsNoteDisabledReason: null, fmsNoteInfo: null };
  }
  if (coverage === "none") {
    return { fmsNoteDisabledReason: "None of the locations here use an FMS that supports writing tenant notes.", fmsNoteInfo: null };
  }
  if (coverage === "some") {
    const supported = fms.supportedCount(FMSCapabilityKey.NoteWrite, objectType);
    return { fmsNoteDisabledReason: null, fmsNoteInfo: `Written only at locations whose FMS supports notes (${supported} of ${fms.locationCount}).` };
  }
  return { fmsNoteDisabledReason: null, fmsNoteInfo: null };
}

/** The right pane: one sequence type's settings at the current scope. Selected type = the URL slug. */
export function SequenceTypePanel(): React.ReactElement {
  const { sequenceSlug } = useParams<{ sequenceSlug: string }>();
  const { scope, config, fms, scheduleGroups, canEdit } = useSequencesV3Context();
  const page = usePropertiesGetLocationIds();
  const type = sequenceTypeForSlug(sequenceSlug);

  const capability = useMemo<DayCapabilityNotes>(
    () => ({
      ...(type ? fmsNoteCapability(type, fms, scope.kind) : { fmsNoteDisabledReason: null, fmsNoteInfo: null }),
      // Decision 41: sequence emails go out from the organization's own Mandrill App.
      emailDisabledReason: fms.emailSenderConfigured ? null : NO_EMAIL_SENDER_REASON
    }),
    [type, fms, scope.kind]
  );

  if (!type || !isSequenceTypeVisible(type, scope.kind, fms.supports)) {
    return <Navigate to={`${page.preamble}/page/sequences/${SEQUENCE_TYPE_META[BotSequenceType.PAST_DUE].slug}`} replace />;
  }

  const meta = SEQUENCE_TYPE_META[type];
  const effective = config.effective[type];
  const draft = config.draft(type);
  const dirtyCount = config.dirtyCount(type);
  const errors = dirtyCount > 0 ? config.validationErrors(type) : [];
  const ownRow = config.ownRow(type);
  const engineOn = isFeatureEnabled(FeatureFlagKey.BotSequenceEngineEnabled);

  const stagedLadderDays = draft.ladder.kind === "set" ? draft.ladder.value.length : null;
  const dirtySummary = [
    draft.mode.kind !== "untouched" ? "sequence on/off" : null,
    stagedLadderDays !== null
      ? `days edited (${stagedLadderDays} ${stagedLadderDays === 1 ? "day" : "days"})`
      : draft.ladder.kind === "revert"
        ? "days reverted"
        : null,
    draft.minGapDays.kind !== "untouched" ? "minimum gap" : null,
    draft.pastDueInactiveOptInStatuses.kind !== "untouched" ? "inactive statuses" : null,
    draft.scheduleSlug.kind !== "untouched" ? "send hours" : null
  ]
    .filter(Boolean)
    .join(", ");

  const handleModeChange = (mode: BotSequenceMode): void => config.setField(type, "mode", mode);
  const handleLadderStage = (ladder: BotSequenceConfigStep[]): void => config.setField(type, "ladder", ladder);

  return (
    <div className="flex min-h-full flex-col">
      <div className="mx-auto flex w-full max-w-[960px] flex-1 flex-col gap-4 p-6">
        <header>
          <div className="flex items-center gap-2">
            <span className="h-3 w-1 shrink-0 bg-grey-900" />
            <h2 className="heading-base-alt text-grey-900">{meta.label}</h2>
          </div>
          <p className="body-sm pl-3 text-grey-600">
            {meta.description} Settings here override the{" "}
            {scope.kind === "location" ? "group and organization" : scope.kind === "group" ? "organization" : "platform defaults"}.
          </p>
        </header>

        {!engineOn && (
          <p className="body-sm rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-yellow-900">
            The sequences engine is not switched on for this organization. Settings save normally; nothing is booked or sent until Swivl enables it.
          </p>
        )}

        {config.error && <p className="body-sm rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">{config.error}</p>}

        {config.loading || !effective ? (
          <p className="body-sm text-grey-500">Loading…</p>
        ) : (
          <>
            <ModeSection
              effective={effective.mode}
              staged={draft.mode}
              legacyEnabledLocations={effective.legacyEnabledLocations ?? []}
              scopeKind={scope.kind}
              canEdit={canEdit}
              onChange={handleModeChange}
              onRevert={() => config.revertField(type, "mode")}
              onDiscard={() => config.discardField(type, "mode")}
            />
            <TypeSettingsSection
              sequenceType={type}
              effective={effective}
              draft={draft}
              scopeKind={scope.kind}
              canEdit={canEdit}
              optInCoverage={fms.coverage(...OPT_IN_CAPABILITY)}
              optInSupportedCount={fms.supportedCount(...OPT_IN_CAPABILITY)}
              locationCount={fms.locationCount}
              hoursHref={`${page.preamble}/page/details-v3/hours`}
              scheduleGroups={scheduleGroups}
              onField={(field, value) => config.setField(type, field, value)}
              onScheduleSlug={(slug) => config.setField(type, "scheduleSlug", slug)}
              onStatuses={(value) => config.setField(type, "pastDueInactiveOptInStatuses", value)}
              onRevert={(field) => config.revertField(type, field)}
              onDiscard={(field) => config.discardField(type, field)}
            />
            <LadderSection
              sequenceType={type}
              effective={effective.ladder}
              staged={draft.ladder}
              scopeKind={scope.kind}
              canEdit={canEdit}
              capability={capability}
              onStage={handleLadderStage}
              onRevert={() => config.revertField(type, "ladder")}
              onDiscard={() => config.discardField(type, "ladder")}
            />
          </>
        )}
      </div>

      <SaveBar
        dirtyCount={dirtyCount}
        dirtySummary={dirtySummary}
        errors={errors}
        saving={config.saving === type}
        canEdit={canEdit}
        canRevertRow={ownRow !== undefined && scope.kind !== "org"}
        onCancel={() => config.cancel(type)}
        onSave={() => config.save(type)}
        onRevertRow={() => config.revertRow(type)}
      />
    </div>
  );
}
